<template>
  <div class="discount-tickets">
    <SDialog v-model:open="dialogVisible">
      <SDialogContent
        class="discount-tickets !w-[70%] !max-w-3xl"
        @escape-key-down.prevent
        @pointer-down-outside.prevent
        @open-auto-focus.prevent
      >
        <SDialogHeader>
          <SDialogTitle>{{ $t("discount.select-ticket") }}</SDialogTitle>
        </SDialogHeader>
        <div class="min-w-0">
          <SInputGroup class="mb-4">
            <SInputGroupInput
              :placeholder="$t('field.search')"
              :model-value="ticketsQuery"
              @update:model-value="(v) => (ticketsQuery = String(v))"
            />
            <SInputGroupAddon align="inline-end">
              <SearchIcon />
            </SInputGroupAddon>
          </SInputGroup>
          <div
            v-for="price in searchTickets"
            :key="price.priceId"
            :class="[
              'flex cursor-pointer items-center justify-between gap-4 border-b border-border px-3 py-3.5 transition-colors',
              isPriceSelected(price.priceId)
                ? 'bg-primary/5'
                : 'hover:bg-muted/50',
            ]"
            @click="
              !isPriceSelected(price.priceId)
                ? selectDiscountTickets(price.priceId)
                : deSelectDiscountTickets(price.priceId)
            "
          >
            <p class="min-w-0 truncate">
              <span class="font-semibold">{{ price.categoryName }}</span>
              <span class="text-muted-foreground">
                · {{ price.priceType }} · {{ price.priceName }}
              </span>
            </p>
            <div class="flex shrink-0 items-center gap-4">
              <span class="font-semibold tabular-nums">
                {{ currency + " " + price.unitPrice.toFixed(2) }}
              </span>
              <CheckIcon
                class="size-5 text-primary"
                style="--s-icon-size: 20px"
                :style="{
                  visibility: isPriceSelected(price.priceId)
                    ? 'visible'
                    : 'hidden',
                }"
              />
            </div>
          </div>
        </div>
        <SDialogFooter>
          <div class="d-flex flex-row fl-s-b w-full">
            <SButton
              type="button"
              variant="outline"
              size="lg"
              @click="cancelSelection()"
            >
              {{ $t("button.cancel") }}
            </SButton>

            <el-pagination
              background
              small
              layout="prev, pager, next"
              :total="totalPssListLength"
              :current-page="currentPage"
              @current-change="changeCurrentPage"
              :page-size="pageSize"
            >
            </el-pagination>

            <SButton type="button" size="lg" @click="confirmSelection()">
              {{ $t("button.confirm") }}
            </SButton>
          </div>
        </SDialogFooter>
      </SDialogContent>
    </SDialog>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref, watchEffect } from "vue";
import { useI18n } from "vue-i18n";
import { ModalComponents, ModalStatus } from "@/constants/modal";
import ModalModule from "@/store/modules/modal";
import { dialogVisibleForStatus } from "@/utils/vue-helpers/modal";
import check from "@/assets/images/svg/green-check.svg";
import { useOrderStore } from "@smeetz/order-store";
import { useOrderMetaStore } from "@/stores/order-meta.store";
import {
  SButton,
  SDialog,
  SDialogContent,
  SDialogFooter,
  SDialogHeader,
  SDialogTitle,
  SInputGroup,
  SInputGroupAddon,
  SInputGroupInput,
} from "@smeetz/ui-core";
import { CheckIcon, SearchIcon } from "@smeetz/ui-icons";

interface IDiscountPrice {
  priceId: string;
  categoryName: string;
  priceName: string;
  unitPrice: number;
  priceType: string;
}

export default defineComponent({
  name: ModalComponents.DiscountSelectTickets,
  components: {
    SButton,
    SDialog,
    SDialogContent,
    SDialogFooter,
    SInputGroup,
    SInputGroupAddon,
    SInputGroupInput,
    CheckIcon,
    SearchIcon,
    SDialogHeader,
    SDialogTitle,
  },
  setup() {
    const { t } = useI18n({});
    const orderStore = useOrderStore();
    const orderMetaStore = useOrderMetaStore();

    const iconStyle = "width: 20px; height: 40px;";
    const iconContent = check;
    const iconAlt = "check";
    const ticketsQuery = ref("");
    let pageSize = ref(5);
    let currentPage = ref(1);
    const changeCurrentPage = (val: number) => {
      currentPage.value = val;
    };

    const dialogVisible = dialogVisibleForStatus(
      ModalStatus.DiscountSelectTickets
    );

    const orderSession = computed(() => orderStore.selectedOrder?.orderSession);
    const orderLines = computed(() => orderSession.value?.orderLines || []);
    const discounts = computed(() => orderSession.value?.discounts || []);
    const totalPssListLength = computed(() => pricesList.value.length);

    // A list of prices of the booking (no duplicates)
    const pricesList = computed<IDiscountPrice[]>(() => {
      const pricesById = new Map<string, IDiscountPrice>();
      for (const line of orderLines.value) {
        const priceId = line.priceId;
        if (!priceId || pricesById.has(priceId)) continue;
        pricesById.set(priceId, {
          priceId,
          categoryName: line.categoryName ?? "",
          priceName: line.priceName ?? "",
          unitPrice: line.overridePrice ?? line.unitPrice ?? 0,
          priceType: line.itemType ?? "",
        });
      }
      return Array.from(pricesById.values());
    });

    // Local working copy initialized from the persisted selection (or from
    // the existing applied discount's line IDs as a fallback).
    const selectedPrices = ref<string[]>([]);

    watchEffect(() => {
      if (selectedPrices.value.length > 0) return;

      // 1. Restore from the persisted selection in the order-meta store
      if (orderMetaStore.discountPriceIds.length > 0) {
        selectedPrices.value = [...orderMetaStore.discountPriceIds];
        return;
      }

      // 2. Fallback: derive from the order's existing manual discount lines
      const discountLineIds = discounts.value
        .map((d) => d.lineId)
        .filter((lineId): lineId is string => Boolean(lineId));
      if (discountLineIds.length === 0) return;

      const derivedPriceIds = orderLines.value
        .filter((line) => discountLineIds.includes(line.id))
        .map((line) => line.priceId)
        .filter((priceId): priceId is string => Boolean(priceId));

      derivedPriceIds.forEach((priceId) => {
        if (!selectedPrices.value.includes(priceId)) {
          selectedPrices.value.push(priceId);
        }
      });
    });

    const searchTickets = computed(() => {
      const start = (currentPage.value - 1) * pageSize.value;
      const end = start + pageSize.value;
      return pricesList.value
        .slice(start, end)
        .filter(
          (price: any) =>
            price.categoryName
              .toLowerCase()
              .includes(ticketsQuery.value.toLowerCase()) ||
            price.priceName
              .toLowerCase()
              .includes(ticketsQuery.value.toLowerCase()) ||
            price.priceType
              .toLowerCase()
              .includes(ticketsQuery.value.toLowerCase()) ||
            String(price.unitPrice)
              .toLowerCase()
              .includes(ticketsQuery.value.toLowerCase())
        );
    });

    const currency = computed(() => {
      return orderSession.value?.currency || "";
    });

    function selectDiscountTickets(priceId: string) {
      selectedPrices.value.push(priceId);
    }

    function deSelectDiscountTickets(priceId: string) {
      const index = selectedPrices.value.findIndex((cat) => cat === priceId);
      if (index > -1) {
        selectedPrices.value.splice(index, 1);
      }
    }

    function confirmSelection() {
      // Persist the selection so Discount.vue's Apply can read it.
      // The actual discount application happens in Discount.vue.
      orderMetaStore.setDiscountPriceIds(selectedPrices.value);
      ModalModule.setStatus(ModalStatus.Hidden);
    }

    function cancelSelection() {
      // Clear the selected tickets and close it
      selectedPrices.value = [];
      ModalModule.setStatus(ModalStatus.Hidden);
    }

    function isPriceSelected(priceId: string) {
      return selectedPrices.value.some((prId) => prId === priceId);
    }

    return {
      t,
      dialogVisible,
      pricesList,
      currency,
      isPriceSelected,
      selectDiscountTickets,
      deSelectDiscountTickets,
      confirmSelection,
      cancelSelection,
      totalPssListLength,
      ticketsQuery,
      searchTickets,
      pageSize,
      currentPage,
      changeCurrentPage,
    };
  },
});
</script>

<style lang="scss" scoped></style>
